import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
} from "reactstrap";
import { useParams } from "react-router-dom";
import { URL } from "../../api";
import axiosInstance from "../../Request";

const InstallmentFip = () => {
  const { cid, lid } = useParams();

  const [installments, setInstallments] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmi, setSelectedEmi] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState("");

  // Preview (view) modal for a single installment row.
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewEmi, setViewEmi] = useState(null);
  const openViewModal = (emi) => {
    setViewEmi(emi);
    setViewModalOpen(true);
  };

  // --- PAGINATION STATES ---
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        // V2: every loan's EMIs are keyed to its own loan (lead -> loan -> emi), so always filter by the
        // current lead's loan. The backend resolves lead_id -> loan_id, so switching loans in the header
        // shows only that loan's schedule. (The old payment/wallet customer-only path is obsolete.)
        const url = `${URL.INSTALLMENT_FIP}?customer_id=${cid}&loan_id=${lid}`;
        const response = await axiosInstance.get(url, { withCredentials: true });
        setInstallments(response.data || []);
        setCurrentPage(1); // Reset to first page on new data fetch
      } catch (error) {
        console.error("Error fetching installments:", error);
      }
    };

    fetchCustomerDetails();
  }, [cid, lid]);

  // --- PAGINATION LOGIC ---
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = installments.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(installments.length / rowsPerPage);

  const openPayModal = (emi) => {
    // Pay the amount actually owed: outstanding principal + interest, plus any penal charge.
    const totalAmount =
      Number(emi.outstanding_amount || 0) + Number(emi.penal_outstanding || 0);
    setSelectedEmi(emi);
    setAmount(totalAmount);
    setPaymentLink("");
    setModalOpen(true);
  };

  const generatePaymentLink = async () => {
    setLinkError("");
    if (!userId) {
      setLinkError("User not identified — please log in again.");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setLinkError("Enter a valid amount.");
      return;
    }
    setLinkLoading(true);
    try {
      const response = await axiosInstance.get(
        `${URL.PAYMENT_LINK_GENERATE}?customer_id=${cid}&user_id=${userId}&amount=${amount}&flags=4`
      );
      const data = response.data || {};
      const pay = data.payInResponse || {};
      // The PayIn API may return the URL under a few different keys.
      const link = pay.link || pay.url || pay.paymentLink || pay?.data?.link;
      if (link) {
        setPaymentLink(link);
      } else {
        setLinkError(data.error || pay.message || "Could not generate the payment link. Please try again.");
      }
    } catch (error) {
      console.error("Error generating payment link:", error);
      setLinkError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to generate the payment link."
      );
    } finally {
      setLinkLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(paymentLink);
    alert("Payment link copied!");
  };

  return (
    <div>
      <Table className="align-items-center table-flush" responsive>
        <thead className="thead-light">
          <tr>
            <th>Emi Number</th>
            <th>Amount</th>
            <th>Principal</th>
            <th>Interest</th>
            <th>Outstanding Amount</th>
            <th>Outstanding Interest</th>
            <th>Outstanding Principal</th>
            <th>Collection Received</th>
            <th>Penal Charge</th>
            <th>DPD</th>
            <th>Paid Status</th>
            <th>EMI Date</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {currentRows.length > 0 ? (
            currentRows.map((item, index) => (
              <tr key={index}>
                <td>{item.emi_number}</td>
                <td>{item.emi_amount}</td>
                <td>{item.principal}</td>
                <td>{item.interest}</td>
                <td>{item.outstanding_amount}</td>
                <td>{item.outstanding_interest}</td>
                <td>{item.outstanding_principal}</td>
                <td>{item.collection_received}</td>
                <td>{item.penal_outstanding}</td>
                <td>{item.dpd}</td>
                <td>{item.status_value}</td>
                <td>{item.emi_date}</td>
                <td>
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <Button
                      color="info"
                      size="sm"
                      outline
                      title="View installment"
                      onClick={() => openViewModal(item)}
                    >
                      <i className="fa-regular fa-eye" />
                    </Button>
                    {(Number(item.emi_status) === 3 || Number(item.dpd) > 0) &&
                      Number(item.outstanding_amount) > 0 &&
                      String(item.status_value || "").toLowerCase() !== "paid" && (
                        <Button
                          color="success"
                          size="sm"
                          onClick={() => openPayModal(item)}
                        >
                          Pay
                        </Button>
                      )}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="13" className="text-center">No installments found</td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* --- PAGINATION UI --- */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center mt-3" style={{ gap: "15px" }}>
          <Button
            size="sm"
            color="outline-primary"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <span style={{ fontSize: "14px", fontWeight: "bold" }}>
            Page {currentPage} of {totalPages}
          </span>
          <Button
            size="sm"
            color="outline-primary"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* 🔥 Payment Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          Generate Payment Link
        </ModalHeader>
        <ModalBody>
          <Label>Emi Amount</Label>
          <Input
            type="text"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          {paymentLink && (
            <>
              <Label className="mt-3">Payment Link</Label>
              <Input value={paymentLink} disabled />
            </>
          )}
          {linkError && (
            <div className="text-danger mt-2" style={{ fontSize: "0.85rem" }}>
              {linkError}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {!paymentLink ? (
            <Button color="primary" onClick={generatePaymentLink} disabled={linkLoading}>
              {linkLoading ? "Generating…" : "Generate Link"}
            </Button>
          ) : (
            <Button color="secondary" onClick={copyLink}>
              Copy Link
            </Button>
          )}
          <Button color="danger" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* 👁 Installment Preview Modal */}
      <Modal isOpen={viewModalOpen} toggle={() => setViewModalOpen(false)} centered size="lg">
        <ModalHeader toggle={() => setViewModalOpen(false)}>
          Installment Details {viewEmi ? `— EMI #${viewEmi.emi_number}` : ""}
        </ModalHeader>
        <ModalBody>
          {viewEmi && (
            <Table className="table-bordered" responsive>
              <tbody>
                {[
                  ["EMI Number", viewEmi.emi_number],
                  ["EMI Date", viewEmi.emi_date],
                  ["Paid Status", viewEmi.status_value],
                  ["DPD", viewEmi.dpd],
                  ["EMI Amount", viewEmi.emi_amount],
                  ["Principal", viewEmi.principal],
                  ["Interest", viewEmi.interest],
                  ["Outstanding Amount", viewEmi.outstanding_amount],
                  ["Outstanding Principal", viewEmi.outstanding_principal],
                  ["Outstanding Interest", viewEmi.outstanding_interest],
                  ["Collection Received", viewEmi.collection_received],
                  ["Penal Charge", viewEmi.penal_outstanding],
                ].map(([label, value]) => (
                  <tr key={label}>
                    <th style={{ width: "45%", background: "#f6f9fc" }}>{label}</th>
                    <td>{value ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setViewModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
export default InstallmentFip;